import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What does 'seeing life as a gift' mean according to this module?",
    options: [
      "Expecting everything to be given to you",
      "Recognizing and appreciating the preciousness of each moment and experience",
      "Taking things for granted",
      "Only being grateful for material possessions"
    ],
    correctAnswer: 1,
    explanation: "Seeing life as a gift means recognizing and appreciating the preciousness of each moment and experience. It involves cultivating awareness that life itself is a precious gift to be cherished."
  },
  {
    question: "According to the module, gratitude involves:",
    options: [
      "Only saying 'thank you' for gifts",
      "A deep appreciation for both the good and challenging aspects of life",
      "Being grateful only when things go well",
      "Ignoring difficulties and focusing only on positives"
    ],
    correctAnswer: 1,
    explanation: "Gratitude involves a deep appreciation for both the good and challenging aspects of life. Even difficult experiences can be sources of growth and learning when viewed through the lens of gratitude."
  },
  {
    question: "What is the relationship between gratitude and well-being?",
    options: [
      "They are not related",
      "Gratitude practices are linked to improved mental and physical health",
      "Gratitude makes people less happy",
      "Well-being comes only from material success"
    ],
    correctAnswer: 1,
    explanation: "Research shows that gratitude practices are linked to improved mental and physical health. People who regularly practice gratitude report higher levels of happiness and life satisfaction."
  },
  {
    question: "The module suggests cultivating gratitude through:",
    options: [
      "Only keeping a gratitude journal",
      "Daily practices like gratitude journaling, mindful appreciation, and thankfulness rituals",
      "Being ungrateful for most things",
      "Focusing only on what you lack"
    ],
    correctAnswer: 1,
    explanation: "Gratitude can be cultivated through various daily practices including gratitude journaling, mindful appreciation of simple things, and developing thankfulness rituals that become part of your routine."
  },
  {
    question: "What is the benefit of seeing life as a gift?",
    options: [
      "It makes you materialistic",
      "It shifts perspective from entitlement to appreciation and wonder",
      "It reduces joy and happiness",
      "It has no impact on daily life"
    ],
    correctAnswer: 1,
    explanation: "Seeing life as a gift shifts your perspective from entitlement to appreciation and wonder. This mindset opens you to experiencing more joy and meaning in everyday moments."
  }
];

const SpiritualModule5QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM5');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/spiritual-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 5 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check">✓</span>
                <span>The practice of gratitude</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Seeing life as a gift</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Daily gratitude practices</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your journey by exploring inner peace and finding harmony!</p>
            <p>Ready to cultivate tranquility amid life's chaos?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Spiritual Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left spiritual-panel">
          <div className="module-header">
            <h1>Module 5 Quiz</h1>
            <p className="module-subtitle">Inner Peace – Cultivating Calm Within</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>

            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? handleExit : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule5QuizPage;

